import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { useTimezone } from "../hook/useTimezone";
import "./TimezoneSelector.css";

// Compute the live offset for a given IANA timezone (e.g. "Asia/Kolkata")
// so the dropdown can show "GMT+5:30" next to each row.
const computeOffsetLabel = (tz) => {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    });
    const part = dtf.formatToParts(new Date()).find((p) => p.type === "timeZoneName");
    return part?.value || "";
  } catch {
    return "";
  }
};

// Live "now" in a given tz, refreshed once a minute. Lets the dropdown
// surface the user's actual local time wherever they pick.
const useTickingNow = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
};

const TimezoneSelector = () => {
  const { timezone, setTimezone, options } = useTimezone();
  const now = useTickingNow();

  const current = useMemo(
    () => options.find((o) => o.value === timezone) || options[0],
    [options, timezone]
  );

  const offsets = useMemo(() => {
    const map = {};
    options.forEach((o) => {
      map[o.value] = computeOffsetLabel(o.value);
    });
    return map;
  }, [options]);

  const localTime = useMemo(() => {
    try {
      return new Intl.DateTimeFormat("en-IN", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(now);
    } catch {
      return "";
    }
  }, [timezone, now]);

  return (
    <div className="dropdown tz-selector">
      <button
        type="button"
        className="tz-pill d-flex align-items-center gap-2"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        aria-label={`Timezone: ${current.label}`}
        title={`${current.label} · ${current.region}`}
      >
        <span className="tz-pill-icon d-inline-flex align-items-center justify-content-center">
          <Icon icon="solar:global-bold-duotone" />
        </span>
        <span className="d-flex flex-column align-items-start lh-1">
          <span className="tz-pill-code">{current.short}</span>
          <span className="tz-pill-time">{localTime}</span>
        </span>
        <Icon icon="solar:alt-arrow-down-linear" className="tz-pill-caret" />
      </button>

      <div className="dropdown-menu dropdown-menu-end tz-menu">
        <div className="tz-menu-header d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <Icon icon="solar:global-bold-duotone" className="tz-menu-header-icon" />
            <div className="d-flex flex-column lh-1">
              <span className="tz-menu-header-title">Display timezone</span>
              <span className="tz-menu-header-sub">All dates render in this zone</span>
            </div>
          </div>
        </div>

        <ul className="tz-menu-list">
          {options.map((o) => {
            const isActive = o.value === timezone;
            return (
              <li key={o.value}>
                <button
                  type="button"
                  className={`tz-menu-item${isActive ? " is-active" : ""}`}
                  onClick={() => setTimezone(o.value)}
                >
                  <span className="tz-menu-code">{o.short}</span>
                  <span className="tz-menu-text d-flex flex-column lh-1">
                    <span className="tz-menu-label">{o.label}</span>
                    <span className="tz-menu-region">{o.region}</span>
                  </span>
                  <span className="tz-menu-offset">{offsets[o.value]}</span>
                  {isActive && (
                    <Icon icon="solar:check-circle-bold" className="tz-menu-check" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default TimezoneSelector;
